import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  IconButton,
  Button,
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  useMediaQuery,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Home as HomeIcon,
  Search,
  AddBox,
  Person,
  Favorite,
  FavoriteBorder,
  ChatBubbleOutline,
  IosShare,
  BookmarkBorder,
  Bookmark,
  Chat,
  Repeat,
  Send,
  Notifications,
  Verified,
  SmartDisplay,
} from "@mui/icons-material";

// Firebase
import { db, auth } from "./firebase";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  setDoc,
  serverTimestamp,
  increment,
  updateDoc,
  arrayUnion,
  arrayRemove,
  limit,
} from "firebase/firestore";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging";

// Componentes
import Publicar from "./Publicar";
import PublicarSelector from "./PublicarSelector";
import PublicarReel from "./PublicarReel";
import { canViewPost, isPublicPost } from "./postAudience";
import { PostDetailsInfo } from "./PostDetails";
import { recordAdEvent } from "./adEvents";
import Reels from "./Reels";
import Buscar from "./Buscar";
import Comentarios from "./Comentarios";
import Perfil from "./Perfil";
import PerfilUsuario from "./PerfilUsuario";
import Mensajes from "./Mensajes";
import Notificaciones from "./Notificaciones";
import AnuncioCard from "./AnuncioCard";
import Ajustes from "./Ajustes";
import Promocionar from "./Promocionar";
import Estadisticas from "./Estadisticas";
import PostMedia from "./PostMedia";
import PostTextBlock from "./PostTextBlock";
import PullToRefresh from "./PullToRefresh";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const SWIPE_NAV_ORDER = ["feed", "buscar", "reels", "mensajes", "perfil"];
const SWIPE_MIN_DISTANCE = 70;
const DEFAULT_PREFERENCES = {
  receiveNotifications: true,
  publicProfile: true,
  autoplayVideos: false,
  language: "es",
  theme: "oscuro",
};

const HOME_TEXT = {
  es: {
    newPost: "Nueva publicación",
    loginToReact: "Debes iniciar sesión para reaccionar",
    loginToSave: "Debes iniciar sesión para guardar",
    loginToRepost: "Debes iniciar sesión para republicar",
    linkCopied: "Enlace copiado al portapapeles ✓",
    linkCopyError: "No se pudo copiar el enlace.",
    postReposted: "Publicación republicada",
    repostError: "No se pudo republicar",
    postSent: "Publicación enviada",
    sendError: "No se pudo enviar la publicación",
    sendToContacts: "Enviar a contactos",
    noContacts: "No sigues a ningún contacto todavía.",
    send: "Enviar",
    close: "Cerrar",
    usernameFallback: "usuario",
    userFallback: "Usuario",
    newActivityFallback: "Tienes actividad nueva",
    sharedPostMessage: "Te compartió una publicación",
    shareFallbackText: "Mira esta publicación en Gexvi",
  },
  en: {
    newPost: "New post",
    loginToReact: "You must sign in to react",
    loginToSave: "You must sign in to save",
    loginToRepost: "You must sign in to repost",
    linkCopied: "Link copied to clipboard ✓",
    linkCopyError: "Could not copy the link.",
    postReposted: "Post reposted",
    repostError: "Could not repost",
    postSent: "Post sent",
    sendError: "Could not send the post",
    sendToContacts: "Send to contacts",
    noContacts: "You aren't following any contacts yet.",
    send: "Send",
    close: "Close",
    usernameFallback: "user",
    userFallback: "User",
    newActivityFallback: "You have new activity",
    sharedPostMessage: "Shared a post with you",
    shareFallbackText: "Check out this post on Gexvi",
  },
};

const canUseNotifications = () =>
  typeof window !== "undefined" && "Notification" in window;

const notifyForeground = ({ title, body, url }) => {
  if (!canUseNotifications() || Notification.permission !== "granted") return;
  const text = String(body || "").trim();
  if (!text) return;
  const notification = new Notification(title || "Gexvi", {
    body: text,
    icon: "/logo192.png",
    tag: "gexvi-foreground",
  });

  if (url) {
    notification.onclick = () => {
      window.focus();
      window.location.assign(url);
    };
  }
};

// La app siempre arranca en el feed; esto solo centraliza la navegación
// entre vistas dentro de la sesión (no persiste entre reinicios).
const setViewPersisted = (setView, value) => {
  setView(value);
};

const Home = () => {
  const theme = useTheme();
  const isDesktopNav = useMediaQuery(theme.breakpoints.up("md"));
  const SIDEBAR_WIDTH = 84;

  const [view, setView] = useState("feed");

  // Al entrar a cualquier sección, el scroll arranca arriba
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);

  const [openPublicar, setOpenPublicar] = useState(false);
  const [openPublishSelector, setOpenPublishSelector] = useState(false);
  const [publishType, setPublishType] = useState("post");
  const [promotePostId, setPromotePostId] = useState(null);
  const [viewRefreshKey, setViewRefreshKey] = useState(0);
  const swipeTouchStartRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const [filteredAdsToShow, setFilteredAdsToShow] = useState([]);

  const currentUser = auth.currentUser;
  const userId = currentUser ? currentUser.uid : "invitado";

  // Función para calcular edad desde fecha de nacimiento
  const calculateAge = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    const birth = new Date(fechaNacimiento);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const [postIdSeleccionado, setPostIdSeleccionado] = useState(null);
  const [openComentarios, setOpenComentarios] = useState(false);
  const [selectedUserUid, setSelectedUserUid] = useState(null);
  const [initialChat, setInitialChat] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [myFollowingIds, setMyFollowingIds] = useState([]);
  const [openSendDialog, setOpenSendDialog] = useState(false);
  const [postToSend, setPostToSend] = useState(null);
  const [sendingToUid, setSendingToUid] = useState("");
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [hiddenPostIds, setHiddenPostIds] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(
    currentUser
      ? {
          displayName: currentUser.displayName || "",
          photoURL: currentUser.photoURL || "",
        }
      : null,
  );
  const [userPreferences, setUserPreferences] = useState(DEFAULT_PREFERENCES);
  const [systemPrefersLight, setSystemPrefersLight] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: light)").matches
      : false,
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");
    const handleChange = (e) => setSystemPrefersLight(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const effectiveTheme = useMemo(() => {
    if (userPreferences.theme === "sistema") {
      return systemPrefersLight ? "claro" : "oscuro";
    }
    return userPreferences.theme;
  }, [userPreferences.theme, systemPrefersLight]);
  const isLightTheme = effectiveTheme === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const dialogBg = isLightTheme ? "#ffffff" : "#0b1016";
  const dialogGradient = isLightTheme
    ? "radial-gradient(circle at top, rgba(0,150,180,0.06) 0%, rgba(255,255,255,1) 55%)"
    : "radial-gradient(circle at top, rgba(0,212,255,0.10) 0%, rgba(11,16,22,1) 50%)";
  const language = userPreferences.language || "es";
  const t = useTranslatedDictionary(HOME_TEXT, language);

  useEffect(() => {
    if (!currentUser) return;
    const userRef = doc(db, "users", currentUser.uid);
    // La fecha de nacimiento (para segmentar anuncios) está en el documento privado.
    let privateBirthDate = "";
    const unsubscribePrivate = onSnapshot(
      doc(db, "users", currentUser.uid, "private", "data"),
      (snap) => {
        privateBirthDate = snap.exists() ? snap.data().fechaNacimiento || "" : "";
        setHiddenPostIds(snap.exists() && Array.isArray(snap.data().hiddenPostIds) ? snap.data().hiddenPostIds : []);
        if (privateBirthDate) {
          setCurrentUserProfile((prev) => ({ ...prev, fechaNacimiento: privateBirthDate }));
        }
      },
      () => {},
    );
    const unsubscribeProfile = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setCurrentUserProfile(
          privateBirthDate ? { ...data, fechaNacimiento: privateBirthDate } : data,
        );
        setMyFollowingIds(Array.isArray(data.following) ? data.following : []);
        setUserPreferences({
          ...DEFAULT_PREFERENCES,
          ...(data?.preferences || {}),
        });
      }
    });
    return () => {
      unsubscribePrivate();
      unsubscribeProfile();
    };
  }, [currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(collection(db, "users"), (snap) => {
      const items = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((u) => u.uid !== currentUser.uid);
      setAllUsers(items);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setUnreadNotifications(0);
      return;
    }

    const notificationsQuery = query(
      collection(db, "users", currentUser.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(100),
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snap) => {
      const unreadCount = snap.docs.reduce((acc, docItem) => {
        const data = docItem.data() || {};
        return acc + (data.read ? 0 : 1);
      }, 0);
      setUnreadNotifications(unreadCount);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleOpenComments = (id) => {
    setPostIdSeleccionado(id);
    setOpenComentarios(true);
  };

  useEffect(() => {
    const q = query(collection(db, "posts"), orderBy("timestamp", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPosts(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  // Cargar anuncios
  useEffect(() => {
    const adsQuery = query(
      collection(db, "ads"),
    );
    const unsubscribe = onSnapshot(adsQuery, (snapshot) => {
      const allAds = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      
      // Filtrar anuncios activos según criterios del usuario
      const userAge = currentUserProfile?.fechaNacimiento 
        ? calculateAge(currentUserProfile.fechaNacimiento) 
        : null;
      const userCountry = currentUserProfile?.pais;

      const filtered = allAds.filter(ad => {
        // Verificar que el anuncio esté activo
        if (!ad.active) return false;
        
        // Verificar que no esté expirado
        const expiryDate = ad.expiresAt?.toDate?.() || new Date(ad.expiresAt);
        if (expiryDate < new Date()) return false;
        
        // Verificar que el usuario esté en el rango de edad
        if (userAge) {
          if (ad.targetAgeMin && userAge < ad.targetAgeMin) return false;
          if (ad.targetAgeMax && userAge > ad.targetAgeMax) return false;
        }
        
        // Verificar que el país coincida
        if (userCountry && ad.targetCountries && ad.targetCountries.length > 0) {
          if (!ad.targetCountries.includes(userCountry)) return false;
        }
        
        return true;
      });

      setFilteredAdsToShow(filtered);
    });
    return () => unsubscribe();
  }, [currentUserProfile]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme-preference", effectiveTheme);
    return () => {
      document.documentElement.removeAttribute("data-theme-preference");
    };
  }, [effectiveTheme]);

  useEffect(() => {
    if (!currentUser) return;

    const params = new URLSearchParams(window.location.search);
    const shouldOpenChat = params.get("openChat") === "1";
    const chatId = params.get("chatId");
    const shouldOpenPost = params.get("openPost") === "1";
    const postId = params.get("postId");

    let unsubscribeForeground = null;

    const registerPushToken = async () => {
      if (!canUseNotifications() || !("serviceWorker" in navigator)) {
        return;
      }
      if (!userPreferences.receiveNotifications) {
        return;
      }
      if (Notification.permission === "denied") {
        return;
      }

      // Solicitar permiso si aun no se ha pedido
      if (Notification.permission === "default") {
        try {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") {
            return;
          }
        } catch (error) {
          console.error("Error solicitando permisos de notificacion", error);
          return;
        }
      }

      if (Notification.permission !== "granted") {
        return;
      }

      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        console.warn("VAPID key no configurada");
        return;
      }

      const supported = await isSupported();
      if (!supported) {
        return;
      }

      try {
        const swRegistration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js",
        );

        const messaging = getMessaging();
        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: swRegistration,
        });

        if (token) {
          await setDoc(
            doc(db, "users", currentUser.uid, "private", "data"),
            {
              fcmToken: token,
              fcmTokens: arrayUnion(token),
              fcmUpdatedAt: serverTimestamp(),
            },
            { merge: true },
          );
        }

        unsubscribeForeground = onMessage(messaging, (payload) => {
          if (!userPreferences.receiveNotifications) return;
          const title = payload?.notification?.title || "Gexvi";
          const body = payload?.notification?.body || t("newActivityFallback");
          const url = payload?.data?.url || "/";
          notifyForeground({ title, body, url });
        });
      } catch (error) {
        console.error("Error en flujo de registro de push:", error);
      }
    };

    registerPushToken().catch((error) => {
      console.error("No se pudo registrar push en Home", error);
    });

    const clearDeepLinkParams = () => {
      params.delete("openChat");
      params.delete("chatId");
      params.delete("openPost");
      params.delete("postId");
      const search = params.toString();
      const nextUrl = `${window.location.pathname}${search ? `?${search}` : ""}${window.location.hash}`;
      window.history.replaceState({}, "", nextUrl);
    };

    const openDeepLinkedChat = async () => {
      try {
        const chatSnap = await getDoc(doc(db, "chats", chatId));
        if (!chatSnap.exists()) {
          setViewPersisted(setView, "mensajes");
          return;
        }

        const chat = chatSnap.data() || {};
        const participants = Array.isArray(chat.participants)
          ? chat.participants
          : [];
        const otherUid =
          participants.find((uid) => uid !== currentUser.uid) || "";

        setInitialChat({
          chatId,
          uid: otherUid,
          displayName: chat.participantNames?.[otherUid] || t("userFallback"),
          photoURL: chat.participantPhotos?.[otherUid] || "",
        });
        setViewPersisted(setView, "mensajes");
      } catch (error) {
        console.error("No se pudo abrir el chat de notificacion", error);
      } finally {
        clearDeepLinkParams();
      }
    };

    if (shouldOpenChat && chatId) {
      openDeepLinkedChat();
    }

    if (shouldOpenPost && postId) {
      setViewPersisted(setView, "feed");
      setPostIdSeleccionado(postId);
      setOpenComentarios(true);
      clearDeepLinkParams();
    }

    return () => {
      if (unsubscribeForeground) unsubscribeForeground();
    };
  }, [currentUser, userPreferences.receiveNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLike = async (postId, likes = []) => {
    if (!currentUser) return alert(t("loginToReact"));
    const postRef = doc(db, "posts", postId);

    if (likes.includes(userId)) {
      await updateDoc(postRef, { likes: arrayRemove(userId) });
    } else {
      await updateDoc(postRef, { likes: arrayUnion(userId) });
    }
  };

  const handleSave = async (postId, savedBy = []) => {
    if (!currentUser) return alert(t("loginToSave"));
    const postRef = doc(db, "posts", postId);

    if (savedBy.includes(userId)) {
      await updateDoc(postRef, { savedBy: arrayRemove(userId) });
    } else {
      await updateDoc(postRef, { savedBy: arrayUnion(userId) });
    }
  };

  const handleShare = async (post) => {
    if (!isPublicPost(post)) return;
    const shareText = post.text
      ? post.text.substring(0, 100)
      : t("shareFallbackText");
    const shareUrl = window.location.href;

    const copyToClipboard = (text) => {
      try {
        const el = document.createElement("textarea");
        el.value = text;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        return true;
      } catch {
        return false;
      }
    };

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Gexvi",
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch {
        // usuario canceló o no soportado, intentar copiar
      }
    }

    let copied = false;
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(shareUrl);
        copied = true;
      } catch {
        copied = copyToClipboard(shareUrl);
      }
    } else {
      copied = copyToClipboard(shareUrl);
    }

    alert(copied ? t("linkCopied") : t("linkCopyError"));
  };

  const extractHashtags = (text = "") => {
    const matches = String(text || "").match(/#[\p{L}\p{N}_]+/gu) || [];
    return matches.map((tag) => tag.toLowerCase());
  };

  const toMillis = (timestamp) => {
    if (!timestamp) return Date.now();
    if (typeof timestamp.toDate === "function") return timestamp.toDate().getTime();
    if (timestamp.seconds) return timestamp.seconds * 1000;
    return Date.now();
  };

  // Perfil de intereses: hashtags usados por las cuentas que el usuario sigue,
  // ponderados por qué tan reciente es cada publicación (los temas de
  // publicaciones recientes de tus seguidos pesan más que los viejos).
  const interestProfile = useMemo(() => {
    const weights = new Map();
    if (!myFollowingIds.length) return weights;
    const followedSet = new Set(myFollowingIds);
    const now = Date.now();
    posts.forEach((post) => {
      if (!followedSet.has(post.uid)) return;
      const tags = extractHashtags(post.text);
      if (!tags.length) return;
      const ageDays = (now - toMillis(post.timestamp)) / (1000 * 60 * 60 * 24);
      const recencyFactor = Math.pow(0.5, ageDays / 14); // vida media de 14 días
      tags.forEach((tag) => {
        weights.set(tag, (weights.get(tag) || 0) + recencyFactor);
      });
    });
    return weights;
  }, [posts, myFollowingIds]);

  const myBlockedUids = useMemo(
    () => (Array.isArray(currentUserProfile?.blockedUids) ? currentUserProfile.blockedUids : []),
    [currentUserProfile?.blockedUids]
  );

  const blockedByUsersSet = useMemo(() => {
    const set = new Set();
    allUsers.forEach((u) => {
      if (Array.isArray(u.blockedUids) && u.blockedUids.includes(currentUser?.uid)) {
        set.add(u.uid);
      }
    });
    return set;
  }, [allUsers, currentUser?.uid]);

  // Muro: se ven publicaciones de todos, pero ordenadas por un puntaje que
  // combina qué tan reciente es el post, cuánto matchea con los temas de las
  // cuentas que seguís, si es de alguien que seguís, y su interacción. Sin
  // intereses detectados (usuario nuevo o sin hashtags) queda como
  // reciente + interacción, para que el feed nunca se vea vacío o raro.
  const rankedPosts = useMemo(() => {
    const followedSet = new Set(myFollowingIds);
    const now = Date.now();
    return posts
      .filter((post) => {
        if (hiddenPostIds.includes(post.id)) return false;
        if (!canViewPost(post, currentUser?.uid, myFollowingIds)) return false;
        if (!post.uid) return true;
        if (myBlockedUids.includes(post.uid)) return false;
        if (blockedByUsersSet.has(post.uid)) return false;
        return true;
      })
      .map((post) => {
        const ageHours = (now - toMillis(post.timestamp)) / (1000 * 60 * 60);
        const recencyScore = Math.pow(0.5, ageHours / 30); // vida media de 30 horas
        const tags = extractHashtags(post.text);
        const topicScore = tags.reduce((sum, tag) => sum + (interestProfile.get(tag) || 0), 0);
        const followedBonus = followedSet.has(post.uid) ? 1 : 0;
        const engagementScore = Math.log(
          1 + (post.likes?.length || 0) + (post.commentsCount || 0) * 2 + (post.repostCount || 0) * 1.5
        );
        const score = recencyScore * 3 + topicScore * 2 + followedBonus + engagementScore * 0.5;
        return { post, score };
      })
      .sort((a, b) => b.score - a.score)
      .map((item) => item.post);
  }, [posts, hiddenPostIds, currentUser?.uid, myFollowingIds, myBlockedUids, blockedByUsersSet, interestProfile]);

  // Anuncios ordenados con el mismo perfil de intereses: los que hablan de
  // temas que le interesan al usuario (según lo que postean sus seguidos)
  // aparecen primero entre los anuncios elegibles por edad/país.
  const rankedAds = useMemo(() => {
    return filteredAdsToShow
      .map((ad) => {
        const tags = extractHashtags(`${ad.title || ""} ${ad.postText || ""}`);
        const topicScore = tags.reduce((sum, tag) => sum + (interestProfile.get(tag) || 0), 0);
        return { ad, score: topicScore + Math.random() * 0.01 };
      })
      .sort((a, b) => b.score - a.score)
      .map((item) => item.ad);
  }, [filteredAdsToShow, interestProfile]);

  // Un reel promocionado sale en Reels con su edición; el resto de los anuncios, en el inicio.
  const sponsoredPostIds = useMemo(
    () => rankedAds.filter((ad) => ad.adType === "post" && ad.postId).map((ad) => ad.postId),
    [rankedAds],
  );
  const sponsoredAdIds = useMemo(() => {
    const map = {};
    rankedAds.forEach((ad) => {
      if (ad.adType === "post" && ad.postId && !map[ad.postId]) map[ad.postId] = ad.id;
    });
    return map;
  }, [rankedAds]);
  const feedAds = useMemo(() => {
    const reelIds = new Set(posts.filter((post) => post.isReel).map((post) => post.id));
    return rankedAds.filter((ad) => !reelIds.has(ad.postId));
  }, [rankedAds, posts]);

  const postProfilesByUid = useMemo(() => {
    const map = {};
    posts.forEach((post) => {
      if (!post?.uid) return;
      if (!map[post.uid]) map[post.uid] = {};
      map[post.uid].displayName =
        map[post.uid].displayName || post.username || "";
      map[post.uid].handle = map[post.uid].handle || post.handle || "";
      map[post.uid].photoURL =
        map[post.uid].photoURL ||
        post.userImg ||
        post.photoURL ||
        post.photoUrl ||
        "";
      map[post.uid].verified =
        typeof map[post.uid].verified === "boolean"
          ? map[post.uid].verified
          : Boolean(post.userVerified || post.verified);
    });
    return map;
  }, [posts]);

  const resolveIdentity = (uid, fallbackName = "") => {
    const userDoc = allUsers.find((u) => u.uid === uid) || {};
    const postProfile = postProfilesByUid[uid] || {};
    const name =
      userDoc.displayName ||
      userDoc.username ||
      postProfile.displayName ||
      userDoc.handle ||
      postProfile.handle ||
      fallbackName ||
      t("userFallback");
    const handle = userDoc.username || userDoc.handle || postProfile.username || postProfile.handle || "";
    const photo =
      userDoc.photoURL ||
      userDoc.photoUrl ||
      userDoc.userImg ||
      userDoc.avatar ||
      postProfile.photoURL ||
      "";
    const verified = Boolean(userDoc.verified || postProfile.verified);
    return { name, handle, photo, verified };
  };

  const followingContacts = useMemo(() => {
    return myFollowingIds
      .map((uid) => {
        const identity = resolveIdentity(uid);
        return { uid, ...identity };
      })
      .filter((contact) => contact.uid);
  }, [myFollowingIds, allUsers, postProfilesByUid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRepost = async (post) => {
    if (!isPublicPost(post)) return;
    if (!currentUser) return alert(t("loginToRepost"));
    try {
      const userSnap = await getDoc(doc(db, "users", currentUser.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};
      const repostMedia = Array.isArray(post.media)
        ? post.media
        : [
            ...(post.imageUrl ? [{ type: "image", url: post.imageUrl }] : []),
            ...(post.videoUrl ? [{ type: "video", url: post.videoUrl }] : []),
          ];
      const repostImageUrl =
        repostMedia.find((item) => item?.type === "image")?.url ||
        post.imageUrl ||
        "";
      const repostVideoUrl =
        repostMedia.find((item) => item?.type === "video")?.url ||
        post.videoUrl ||
        "";
      await addDoc(collection(db, "posts"), {
        uid: currentUser.uid,
        username: userData.displayName || currentUser.displayName || t("userFallback"),
        handle:
          userData.username || currentUser.email?.split("@")[0] || "usuario",
        userImg: userData.photoURL || currentUser.photoURL || "",
        userVerified: Boolean(userData.verified),
        text: post.text || "",
        media: repostMedia,
        imageUrl: repostImageUrl,
        videoUrl: repostVideoUrl,
        likes: [],
        savedBy: [],
        commentsCount: 0,
        isRepost: true,
        originalPostId: post.id,
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, "posts", post.id), { repostCount: increment(1) });
      alert(t("postReposted"));
    } catch (error) {
      console.error(error);
      alert(t("repostError"));
    }
  };

  const handleOpenSendDialog = (post) => {
    if (!isPublicPost(post)) return;
    setPostToSend(post);
    setOpenSendDialog(true);
  };

  const handleSendPostToContact = async (targetUid) => {
    if (!currentUser || !postToSend || !targetUid || sendingToUid) return;
    setSendingToUid(targetUid);
    try {
      const target = resolveIdentity(targetUid);
      const myName =
        currentUserProfile?.displayName ||
        currentUser.displayName ||
        currentUser.email?.split("@")[0] ||
        "Yo";
      const myPhoto =
        currentUserProfile?.photoURL || currentUser.photoURL || "";
      const chatId = [currentUser.uid, targetUid].sort().join("_");

      await setDoc(
        doc(db, "chats", chatId),
        {
          participants: [currentUser.uid, targetUid],
          members: [currentUser.uid, targetUid],
          participantNames: {
            [currentUser.uid]: myName,
            [targetUid]: target.name,
          },
          participantPhotos: {
            [currentUser.uid]: myPhoto,
            [targetUid]: target.photo,
          },
          lastMessage: t("sharedPostMessage"),
          lastMessageSenderUid: currentUser.uid,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      await addDoc(collection(db, "chats", chatId, "messages"), {
        uid: currentUser.uid,
        text: `${t("sharedPostMessage")}: ${postToSend.text || t("shareFallbackText")}`,
        sharedPostId: postToSend.id,
        sharedPostText: postToSend.text || "",
        sharedPostImage:
          postToSend.imageUrl ||
          postToSend.media?.find((item) => item?.type === "image")?.url ||
          postToSend.media?.[0]?.url ||
          "",
        timestamp: serverTimestamp(),
      });

      await updateDoc(doc(db, "posts", postToSend.id), {
        sentCount: increment(1),
      });

      setOpenSendDialog(false);
      setPostToSend(null);
      alert(t("postSent"));
    } catch (error) {
      console.error(error);
      alert(t("sendError"));
    } finally {
      setSendingToUid("");
    }
  };

  const handleRefreshFeed = async () => {
    try {
      const postsSnap = await getDocs(
        query(collection(db, "posts"), orderBy("timestamp", "desc")),
      );
      setPosts(postsSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));

      const adsSnap = await getDocs(collection(db, "ads"));
      const userAge = currentUserProfile?.fechaNacimiento
        ? calculateAge(currentUserProfile.fechaNacimiento)
        : null;
      const userCountry = currentUserProfile?.pais;
      const filtered = adsSnap.docs
        .map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        .filter((ad) => {
          if (!ad.active) return false;
          const expiryDate = ad.expiresAt?.toDate?.() || new Date(ad.expiresAt);
          if (expiryDate < new Date()) return false;
          if (userAge) {
            if (ad.targetAgeMin && userAge < ad.targetAgeMin) return false;
            if (ad.targetAgeMax && userAge > ad.targetAgeMax) return false;
          }
          if (userCountry && ad.targetCountries?.length > 0) {
            if (!ad.targetCountries.includes(userCountry)) return false;
          }
          return true;
        });
      setFilteredAdsToShow(filtered);
    } catch (error) {
      console.error("No se pudo refrescar el feed", error);
    }
  };

  // Refresco genérico para el resto de pantallas: al no tener acceso a los
  // listeners internos de cada vista, forzamos su remonte (cambiando su
  // "key") para que vuelvan a suscribirse/cargar sus datos desde cero.
  const handleRefreshView = async () => {
    if (view === "feed") {
      await handleRefreshFeed();
      return;
    }
    setViewRefreshKey((k) => k + 1);
    await new Promise((resolve) => setTimeout(resolve, 350));
  };

  const getSwipeNavIndex = () => SWIPE_NAV_ORDER.indexOf(view);

  const goToSwipeNavIndex = (index) => {
    const target = SWIPE_NAV_ORDER[index];
    if (target !== "mensajes") setInitialChat(null);
    setViewPersisted(setView, target);
  };

  const isInsideOverlay = (target) =>
    Boolean(target?.closest?.(".MuiModal-root, .MuiPopover-root, .MuiMenu-root"));

  const handleEditPostText = (postId, text) =>
    updateDoc(doc(db, "posts", postId), { text });

  const handleDeletePost = (postId) => deleteDoc(doc(db, "posts", postId));

  const handleHidePost = (postId) =>
    setDoc(
      doc(db, "users", currentUser.uid, "private", "data"),
      { hiddenPostIds: arrayUnion(postId) },
      { merge: true },
    );

  const handleBlockUser = (uid) =>
    updateDoc(doc(db, "users", currentUser.uid), {
      blockedUids: arrayUnion(uid),
      ...(myFollowingIds.includes(uid)
        ? { following: arrayRemove(uid), followingCount: increment(-1) }
        : {}),
    });

  // Seguridad infantil va al canal que ya avisa por correo; el resto queda en postReports.
  const handleReportPost = (post, reason) => {
    const contentUrl = post.videoUrl || post.media?.find((item) => item?.type === "video")?.url || "";
    if (reason === "child_safety") {
      return addDoc(collection(db, "childSafetyReports"), {
        reporterUid: currentUser.uid,
        reporterEmail: currentUser.email || "",
        reporterDisplayName: currentUser.displayName || "",
        type: "content",
        description: `Reporte desde Reels. Publicación ${post.id}, autor ${post.uid}.`,
        contentUrl: contentUrl || null,
        status: "new",
        createdAt: serverTimestamp(),
      });
    }
    return addDoc(collection(db, "postReports"), {
      postId: post.id,
      postOwnerUid: post.uid,
      reporterUid: currentUser.uid,
      reason,
      contentUrl,
      status: "new",
      createdAt: serverTimestamp(),
    });
  };

  // Foto del usuario en la navegación; sin foto, Avatar muestra el ícono de persona.
  const profileNavIcon = (size) => {
    const isActive = view === "perfil";
    return (
      <Avatar
        src={currentUserProfile?.photoURL || currentUser?.photoURL || ""}
        sx={{
          width: size,
          height: size,
          bgcolor: "transparent",
          color: isActive ? "#00d4ff" : onSurface(1),
          outline: isActive ? "2px solid #00d4ff" : "none",
          outlineOffset: 1,
        }}
      >
        <Person />
      </Avatar>
    );
  };

  const getPostAuthor = (post) => {
    const isCurrentUser = currentUser && post.uid === currentUser.uid;
    const identity = isCurrentUser
      ? {
          name: currentUserProfile?.displayName || post.username,
          photo: currentUserProfile?.photoURL || post.userImg || "",
        }
      : resolveIdentity(post.uid, post.username);
    return {
      name: identity.name || post.username || t("userFallback"),
      photo: identity.photo || post.userImg || "",
      verified: Boolean(
        identity.verified ||
        (isCurrentUser && currentUserProfile?.verified) ||
        post.userVerified
      ),
    };
  };

  const handleSwipeNavTouchStart = (e) => {
    if (isDesktopNav || openComentarios || openSendDialog || e.touches.length !== 1 || isInsideOverlay(e.target)) return;
    const touch = e.touches[0];
    swipeTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleSwipeNavTouchEnd = (e) => {
    if (isDesktopNav || openComentarios || openSendDialog || isInsideOverlay(e.target)) return;
    const start = swipeTouchStartRef.current;
    swipeTouchStartRef.current = null;
    if (!start) return;

    const touch = e.changedTouches[0];
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    if (Math.abs(dx) < SWIPE_MIN_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const currentIndex = getSwipeNavIndex();
    if (currentIndex === -1) return;

    const nextIndex = currentIndex + (dx > 0 ? 1 : -1);
    if (nextIndex < 0 || nextIndex >= SWIPE_NAV_ORDER.length) return;

    goToSwipeNavIndex(nextIndex);
  };

  return (
    <Box
      onTouchStart={handleSwipeNavTouchStart}
      onTouchEnd={handleSwipeNavTouchEnd}
      sx={{
        background: isLightTheme
          ? "linear-gradient(to bottom, #ffffff, #f2f5f8)"
          : "linear-gradient(to bottom, #101010, #000)",
        minHeight: "100vh",
        color: onSurface(1),
        pb: isDesktopNav ? 0 : 12,
        pl: isDesktopNav ? `${SIDEBAR_WIDTH}px` : 0,
      }}
    >
      <PullToRefresh
        onRefresh={handleRefreshView}
        isLightTheme={isLightTheme}
        disabled={openPublicar || openComentarios || openSendDialog || view === "reels"}
      >
      {/* --- VISTA DE FEED --- */}
      {view === "feed" && (
        <>
          <Box
            sx={{
              px: 2,
              py: 1,
              display: "flex",
              justifyContent: "center",
              position: "sticky",
              top: 0,
              zIndex: 10,
              backdropFilter: "blur(20px)",
              backgroundColor: isLightTheme ? "#fff" : "#000",
              borderBottom: "none",
              boxShadow: "none",
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "600px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Box component="img" src="/logo192.png" alt="Gexvi" sx={{ height: 28, width: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Gexvi
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  aria-label={t("newPost")}
                  onClick={() => setOpenPublishSelector(true)}
                  sx={{ p: 0.75 }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "8px",
                      bgcolor: "#00d4ff",
                      display: "grid",
                      placeItems: "center",
                      boxShadow: "0 8px 18px rgba(0,212,255,0.35)",
                    }}
                  >
                    <AddBox sx={{ color: "#041015", fontSize: 22 }} />
                  </Box>
                </IconButton>
                <IconButton
                  onClick={() => setViewPersisted(setView, "notificaciones")}
                  sx={{ color: onSurface(1) }}
                >
                  <Badge
                    color="error"
                    badgeContent={unreadNotifications > 99 ? "99+" : unreadNotifications}
                    invisible={!unreadNotifications}
                  >
                    <Notifications
                      sx={{ color: view === "notificaciones" ? "#00d4ff" : onSurface(1) }}
                    />
                  </Badge>
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", px: 2 }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: "600px",
                position: "relative",
                mt: 2,
              }}
            >
              {/* Combinar posts y anuncios intercalados, ambos ordenados por el
                  perfil de intereses derivado de las cuentas que seguís */}
              {(() => {
                const items = [];
                let adIndex = 0;

                // Los reels tienen edición (recorte, filtros, texto) que solo aplica la sección de Reels.
                rankedPosts.filter((post) => !post.isReel).forEach((post, postIndex) => {
                  items.push({ type: 'post', data: post });
                  // Insertar un anuncio cada 3 posts
                  if ((postIndex + 1) % 3 === 0 && adIndex < feedAds.length) {
                    items.push({ type: 'ad', data: feedAds[adIndex] });
                    adIndex++;
                  }
                });

                return items.map((item, itemIndex) => {
                  if (item.type === 'ad') {
                    return (
                      <AnuncioCard
                        key={`ad-${item.data.id}`}
                        ad={item.data}
                        isLightTheme={isLightTheme}
                        language={language}
                        onClickAd={(adId) => recordAdEvent(adId, "click")}
                        onOpenAdvertiserProfile={(ownerUid) => {
                          if (!ownerUid) return;
                          if (ownerUid === currentUser?.uid) {
                            setViewPersisted(setView, "perfil");
                            return;
                          }
                          setSelectedUserUid(ownerUid);
                          setView("perfilUsuario");
                        }}
                      />
                    );
                  }

                  const post = item.data;
                  const hasLiked = post.likes?.includes(userId);
                  const hasSaved = post.savedBy?.includes(userId);
                  const {
                    name: displayName,
                    photo: displayPhoto,
                    verified: isAuthorVerified,
                  } = getPostAuthor(post);

                  return (
                    <Box
                      key={post.id}
                      sx={{
                        mb: 5,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Paper
                        sx={{
                          bgcolor: onSurface(isLightTheme ? 0.045 : 0.05),
                          p: 2,
                          borderRadius: "20px",
                          color: onSurface(1),
                          backdropFilter: "blur(10px)",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1, cursor: "pointer" }}
                          onClick={() => {
                            if (post.uid !== currentUser?.uid) {
                              setSelectedUserUid(post.uid);
                              setView("perfilUsuario");
                            } else {
                              setViewPersisted(setView, "perfil");
                            }
                          }}
                        >
                          <Avatar src={displayPhoto} sx={{ width: 32, height: 32 }} />
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {displayName}
                            {isAuthorVerified ? (
                              <Verified sx={{ fontSize: 15, color: "#35b6ff" }} />
                            ) : null}
                          </Typography>
                        </Stack>

                        <PostMedia
                          post={post}
                          maxHeight={340}
                          autoplayVideos={userPreferences.autoplayVideos}
                          isLightTheme={isLightTheme}
                          language={language}
                        />

                        <PostTextBlock text={post.text} textBackground={post.textBackground} isLightTheme={isLightTheme} />

                        <PostDetailsInfo
                          post={post}
                          isLightTheme={isLightTheme}
                          language={language}
                          onOpenProfile={(uid) => {
                            if (uid === currentUser?.uid) {
                              setViewPersisted(setView, "perfil");
                              return;
                            }
                            setSelectedUserUid(uid);
                            setView("perfilUsuario");
                          }}
                        />

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ pt: 1 }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.35}
                            alignItems="center"
                          >
                            <IconButton
                              size="small"
                              sx={{
                                color: hasLiked ? "#ff4b4b" : onSurface(1),
                                p: 0.45,
                              }}
                              onClick={() => handleLike(post.id, post.likes)}
                            >
                              {hasLiked ? (
                                <Favorite fontSize="small" />
                              ) : (
                                <FavoriteBorder fontSize="small" />
                              )}
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{ mr: 0.35, fontSize: "0.68rem" }}
                            >
                              {post.likes?.length || 0}
                            </Typography>

                            <IconButton
                              size="small"
                              sx={{ color: onSurface(1), p: 0.45 }}
                              onClick={() => handleOpenComments(post.id)}
                            >
                              <ChatBubbleOutline fontSize="small" />
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{ mr: 0.35, fontSize: "0.68rem" }}
                            >
                              {post.commentsCount || 0}
                            </Typography>

                            {/* Republicar, enviar o compartir sacaría una publicación privada de su público */}
                            {isPublicPost(post) && (
                            <>
                            <IconButton
                              size="small"
                              sx={{ color: onSurface(1), p: 0.45 }}
                              onClick={() => handleRepost(post)}
                            >
                              <Repeat fontSize="small" />
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{ mr: 0.35, fontSize: "0.68rem" }}
                            >
                              {post.repostCount || 0}
                            </Typography>

                            <IconButton
                              size="small"
                              sx={{ color: onSurface(1), p: 0.45 }}
                              onClick={() => handleOpenSendDialog(post)}
                            >
                              <Send fontSize="small" />
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{ fontSize: "0.68rem" }}
                            >
                              {post.sentCount || 0}
                            </Typography>
                            </>
                            )}
                          </Stack>

                          <Stack direction="row" spacing={0.2}>
                            <IconButton
                              size="small"
                              sx={{
                                color: hasSaved ? "#00d4ff" : onSurface(1),
                                p: 0.45,
                              }}
                              onClick={() => handleSave(post.id, post.savedBy)}
                            >
                              {hasSaved ? (
                                <Bookmark fontSize="small" />
                              ) : (
                                <BookmarkBorder fontSize="small" />
                              )}
                            </IconButton>
                            {isPublicPost(post) && (
                            <IconButton
                              size="small"
                              sx={{ color: onSurface(1), p: 0.45 }}
                              onClick={() => handleShare(post)}
                            >
                              <IosShare fontSize="small" />
                            </IconButton>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    </Box>
                  );
                });
              })()}
            </Box>
          </Box>
        </>
      )}

      {/* --- VISTAS ALTERNATIVAS --- */}
      {view === "reels" && (
        <Reels
          posts={rankedPosts}
          userId={userId}
          language={language}
          sidebarWidth={isDesktopNav ? SIDEBAR_WIDTH : 0}
          paused={openComentarios || openSendDialog}
          sponsoredPostIds={sponsoredPostIds}
          sponsoredAdIds={sponsoredAdIds}
          onPromotePost={(postId) => {
            setPromotePostId(postId);
            setViewPersisted(setView, "promoverReel");
          }}
          getAuthor={getPostAuthor}
          onLike={handleLike}
          onComment={handleOpenComments}
          onRepost={handleRepost}
          onSend={handleOpenSendDialog}
          onSave={handleSave}
          onShare={handleShare}
          onEditPost={handleEditPostText}
          onDeletePost={handleDeletePost}
          onNotInterested={handleHidePost}
          onBlockUser={handleBlockUser}
          onReportPost={handleReportPost}
          onOpenProfile={(uid) => {
            if (uid === currentUser?.uid) {
              setViewPersisted(setView, "perfil");
              return;
            }
            setSelectedUserUid(uid);
            setView("perfilUsuario");
          }}
        />
      )}

      {view === "buscar" && (
        <Buscar
          key={viewRefreshKey}
          onBack={() => setViewPersisted(setView, "feed")}
          language={language}
          themeMode={effectiveTheme}
          onOpenProfile={(uid) => {
            if (!uid) return;
            if (uid === currentUser?.uid) {
              setViewPersisted(setView, "perfil");
              return;
            }
            setSelectedUserUid(uid);
            setView("perfilUsuario");
          }}
        />
      )}
      {view === "perfil" && (
        <Perfil
          key={viewRefreshKey}
          onBack={() => setViewPersisted(setView, "feed")}
          onOpenSettings={() => setViewPersisted(setView, "ajustes")}
          onNavigate={(newView) => setViewPersisted(setView, newView)}
          language={language}
          themeMode={effectiveTheme}
          onOpenProfile={(uid) => {
            if (!uid) return;
            if (uid === currentUser?.uid) {
              setViewPersisted(setView, "perfil");
              return;
            }
            setSelectedUserUid(uid);
            setView("perfilUsuario");
          }}
        />
      )}
      {view === "ajustes" && (
        <Ajustes
          key={viewRefreshKey}
          onBack={() => setViewPersisted(setView, "perfil")}
          onNavigate={(newView) => setViewPersisted(setView, newView)}
          language={language}
          themeMode={effectiveTheme}
          preferences={userPreferences}
          onPreferencesSaved={(nextPreferences) => {
            setUserPreferences({ ...DEFAULT_PREFERENCES, ...nextPreferences });
          }}
        />
      )}
      {(view === "promover" || view === "promoverDesdeInicio" || view === "promoverReel") && (
        <Promocionar
          key={viewRefreshKey}
          initialPostId={view === "promoverReel" ? promotePostId : null}
          onBack={() => {
            const backViews = { promover: "ajustes", promoverDesdeInicio: "feed", promoverReel: "reels" };
            setViewPersisted(setView, backViews[view]);
          }}
          language={language}
          themeMode={effectiveTheme}
        />
      )}
      {view === "estadisticas" && (
        <Estadisticas
          key={viewRefreshKey}
          onBack={() => setViewPersisted(setView, "ajustes")}
          language={language}
          themeMode={effectiveTheme}
        />
      )}
      {view === "perfilUsuario" && selectedUserUid && (
        <PerfilUsuario
          key={`${selectedUserUid}-${viewRefreshKey}`}
          uid={selectedUserUid}
          language={language}
          themeMode={effectiveTheme}
          onBack={() => setViewPersisted(setView, "feed")}
          onOpenProfile={(targetUid) => {
            if (!targetUid) return;
            if (targetUid === currentUser?.uid) {
              setViewPersisted(setView, "perfil");
              return;
            }
            setSelectedUserUid(targetUid);
            setView("perfilUsuario");
          }}
          onOpenChat={(profile) => {
            setInitialChat({
              uid: profile.uid,
              displayName: profile.displayName,
              photoURL: profile.photoURL,
            });
            setViewPersisted(setView, "mensajes");
          }}
        />
      )}
      {view === "mensajes" && (
        <Mensajes
          key={viewRefreshKey}
          onBack={() => {
            setInitialChat(null);
            setViewPersisted(setView, "feed");
          }}
          initialChat={initialChat}
          allowNotifications={userPreferences.receiveNotifications}
          language={language}
          themeMode={effectiveTheme}
        />
      )}
      {view === "notificaciones" && (
        <Notificaciones
          key={viewRefreshKey}
          onBack={() => setViewPersisted(setView, "feed")}
          language={language}
          onOpenChat={async (chatId) => {
            if (!chatId || !currentUser) return;
            try {
              const chatSnap = await getDoc(doc(db, "chats", chatId));
              if (!chatSnap.exists()) {
                setViewPersisted(setView, "mensajes");
                return;
              }

              const chat = chatSnap.data() || {};
              const participants = Array.isArray(chat.participants)
                ? chat.participants
                : [];
              const otherUid =
                participants.find((uid) => uid !== currentUser.uid) || "";

              setInitialChat({
                chatId,
                uid: otherUid,
                displayName: chat.participantNames?.[otherUid] || t("userFallback"),
                photoURL: chat.participantPhotos?.[otherUid] || "",
              });
              setViewPersisted(setView, "mensajes");
            } catch (error) {
              console.error(
                "No se pudo abrir chat desde notificaciones",
                error,
              );
            }
          }}
          onOpenPost={(postId) => {
            if (!postId) return;
            setViewPersisted(setView, "feed");
            setPostIdSeleccionado(postId);
            setOpenComentarios(true);
          }}
          onOpenProfile={(uid) => {
            if (!uid) return;
            if (uid === currentUser?.uid) {
              setViewPersisted(setView, "perfil");
              return;
            }
            setSelectedUserUid(uid);
            setView("perfilUsuario");
          }}
          themeMode={effectiveTheme}
        />
      )}
      </PullToRefresh>

      {/* --- MODALES --- */}
      <PublicarSelector
        open={openPublishSelector}
        language={language}
        themeMode={effectiveTheme}
        onClose={() => setOpenPublishSelector(false)}
        onSelect={(type) => {
          setOpenPublishSelector(false);
          if (type === "promotion") {
            setViewPersisted(setView, "promoverDesdeInicio");
            return;
          }
          setPublishType(type);
          setOpenPublicar(true);
        }}
      />

      {openPublicar && publishType === "reel" && (
        <PublicarReel
          language={language}
          people={allUsers.filter((person) => !myBlockedUids.includes(person.uid) && !blockedByUsersSet.has(person.uid))}
          onClose={() => setOpenPublicar(false)}
          onSuccess={() => {
            setOpenPublicar(false);
            setViewPersisted(setView, "reels");
          }}
        />
      )}

      {openPublicar && publishType === "post" && (
      <Publicar
        open
        people={allUsers.filter((person) => !myBlockedUids.includes(person.uid) && !blockedByUsersSet.has(person.uid))}
        language={language}
        themeMode={effectiveTheme}
        handleClose={() => {
          setOpenPublicar(false);
          setViewPersisted(setView, "feed");
        }}
        onSuccess={() => {
          setOpenPublicar(false);
          setViewPersisted(setView, "feed");
        }}
      />
      )}

      <Comentarios
        open={openComentarios}
        handleClose={() => setOpenComentarios(false)}
        postId={postIdSeleccionado}
        onOpenProfile={(uid) => {
          if (!uid) return;
          if (uid === currentUser?.uid) {
            setViewPersisted(setView, "perfil");
            return;
          }
          setSelectedUserUid(uid);
          setView("perfilUsuario");
        }}
        language={language}
        themeMode={effectiveTheme}
      />

      <Dialog
        open={openSendDialog}
        onClose={() => !sendingToUid && setOpenSendDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: dialogGradient,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("sendToContacts")}</DialogTitle>
        <DialogContent>
          {followingContacts.length === 0 ? (
            <Typography sx={{ color: onSurface(0.65), mt: 1 }}>
              {t("noContacts")}
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {followingContacts.map((contact) => (
                <Box
                  key={contact.uid}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    borderRadius: "12px",
                    bgcolor: onSurface(0.03),
                    border: `1px solid ${onSurface(0.07)}`,
                  }}
                >
                  <Avatar src={contact.photo} sx={{ width: 38, height: 38 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                      {contact.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: onSurface(0.55), display: "block" }}
                    >
                      @{contact.handle || t("usernameFallback")}
                    </Typography>
                  </Box>
                  <Button
                    onClick={() => handleSendPostToContact(contact.uid)}
                    disabled={sendingToUid === contact.uid}
                    sx={{
                      textTransform: "none",
                      minWidth: 88,
                      borderRadius: "10px",
                      fontWeight: 600,
                      bgcolor: "#00d4ff",
                      color: "#001217",
                      "&:hover": { bgcolor: "#00b8d9" },
                    }}
                  >
                    {sendingToUid === contact.uid ? (
                      <CircularProgress size={16} />
                    ) : (
                      t("send")
                    )}
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenSendDialog(false)}
            disabled={Boolean(sendingToUid)}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      {isDesktopNav ? (
        /* Nav Lateral Fijo (escritorio) */
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            bottom: 0,
            width: SIDEBAR_WIDTH,
            zIndex: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            bgcolor: isLightTheme ? "#fff" : "#000",
            backdropFilter: "blur(20px)",
            borderRight: "none",
          }}
        >
          <IconButton
            onClick={() => setViewPersisted(setView, "feed")}
            sx={{ borderRadius: "14px", p: 1.5 }}
          >
            <HomeIcon sx={{ color: view === "feed" ? "#00d4ff" : onSurface(1), fontSize: 27 }} />
          </IconButton>
          <IconButton
            onClick={() => setViewPersisted(setView, "buscar")}
            sx={{ borderRadius: "14px", p: 1.5 }}
          >
            <Search sx={{ color: view === "buscar" ? "#00d4ff" : onSurface(1), fontSize: 27 }} />
          </IconButton>
          <IconButton
            onClick={() => setViewPersisted(setView, "reels")}
            sx={{ borderRadius: "14px", p: 1.5 }}
          >
            <SmartDisplay sx={{ color: view === "reels" ? "#00d4ff" : onSurface(1), fontSize: 27 }} />
          </IconButton>
          <IconButton
            onClick={() => {
              setInitialChat(null);
              setViewPersisted(setView, "mensajes");
            }}
            sx={{ borderRadius: "14px", p: 1.5 }}
          >
            <Chat sx={{ color: view === "mensajes" ? "#00d4ff" : onSurface(1), fontSize: 27 }} />
          </IconButton>
          <IconButton
            onClick={() => {
              setInitialChat(null);
              setViewPersisted(setView, "perfil");
            }}
            sx={{ borderRadius: "14px", p: 1.5 }}
          >
            {profileNavIcon(32)}
          </IconButton>
        </Box>
      ) : (
        /* Nav Inferior Fijo (móvil) */
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "calc(100% - 24px)",
            maxWidth: "390px",
            zIndex: 300,
            pointerEvents: "none",
          }}
        >
          <BottomNavigation
            sx={{
              bgcolor: isLightTheme ? "#fff" : "#000",
              backdropFilter: "blur(20px)",
              borderRadius: "22px 22px 0 0",
              height: 64,
              border: "none",
              boxShadow: isLightTheme
                ? "0 18px 38px rgba(15,20,32,0.14)"
                : "0 18px 38px rgba(0,0,0,0.45)",
              display: "flex",
              justifyContent: "space-between",
              px: 1.2,
              pointerEvents: "auto",
              "& .MuiBottomNavigationAction-root": {
                minWidth: 0,
                maxWidth: "none",
                flex: 1,
                px: 0.2,
                borderRadius: "999px",
                color: onSurface(0.76),
                transition: "transform 0.2s ease, color 0.2s ease",
              },
              "& .MuiBottomNavigationAction-root.Mui-selected": {
                color: "#00d4ff",
              },
              "& .MuiBottomNavigationAction-root:hover": {
                transform: "translateY(-1px)",
              },
            }}
            value={view}
            onChange={(e, newValue) => {
              if (newValue !== "mensajes") setInitialChat(null);
              setViewPersisted(setView, newValue);
            }}
          >
            <BottomNavigationAction
              value="feed"
              icon={
                <HomeIcon sx={{ color: view === "feed" ? "#00d4ff" : onSurface(1) }} />
              }
            />
            <BottomNavigationAction
              value="buscar"
              icon={
                <Search sx={{ color: view === "buscar" ? "#00d4ff" : onSurface(1) }} />
              }
            />
            <BottomNavigationAction
              value="reels"
              icon={
                <SmartDisplay sx={{ color: view === "reels" ? "#00d4ff" : onSurface(1) }} />
              }
            />
            <BottomNavigationAction
              value="mensajes"
              icon={
                <Chat sx={{ color: view === "mensajes" ? "#00d4ff" : onSurface(1) }} />
              }
            />
            <BottomNavigationAction
              value="perfil"
              icon={profileNavIcon(28)}
            />
          </BottomNavigation>
        </Box>
      )}
    </Box>
  );
};

export default Home;
